import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from '../../../../../../../../shared/shared.module';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';

export interface SubmittalsRegistryEntry {
  id: string;
  section_no: string;
  sub_section_no: string | null;
  title: string;
  description: string | null;
}

export interface AddSubmittalsRegistryDialogData {
  projectId: string;
  existingRegistries: Pick<SubmittalsRegistryEntry, 'section_no' | 'sub_section_no'>[];
  title?: string | null;
}

type RegistryFormValues = Omit<SubmittalsRegistryEntry, 'id'>;

// Lets a user add a submittal registry entry without leaving the Create submittal panel.
// Closes with the created entry (or null on cancel) so the caller can select it.
@Component({
  selector: 'app-add-submittals-registry-dialog',
  templateUrl: './add-submittals-registry-dialog.component.html',
  styleUrls: ['./add-submittals-registry-dialog.component.scss'],
  standalone: true,
  imports: [SharedModule, MatIconModule],
})
export class AddSubmittalsRegistryDialogComponent {
  readonly dialogRef = inject<MatDialogRef<AddSubmittalsRegistryDialogComponent, SubmittalsRegistryEntry | null>>(MatDialogRef);
  readonly data = inject<AddSubmittalsRegistryDialogData>(MAT_DIALOG_DATA);
  private readonly commonService = inject(CommonService);
  private readonly projectsService = inject(ProjectsService);

  errorMessageMap = {
    section_no: {
      required: 'This field is required.',
      duplicate: 'This section already exists in the registry. Pick it from the list instead.',
    },
    title: {
      required: 'This field is required.',
    },
  };
  errorMessages: Record<string, string> = {};
  form = new FormGroup({
    section_no: new FormControl<string | null>(null, [Validators.required]),
    sub_section_no: new FormControl<string | null>(null),
    title: new FormControl<string | null>(this.data?.title || null, [Validators.required]),
    description: new FormControl<string | null>(null),
  });
  saving = false;
  saveError = '';

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...(currentField ? { ...this.errorMessages, [currentField]: '' } : {}),
      ...this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField),
    };
  }

  cancel() {
    this.dialogRef.close(null);
  }

  save() {
    if (this.saving) {
      return;
    }

    const values = this.trimmedValues();
    this.form.patchValue(values, { emitEvent: false });

    if (this.isDuplicate(values)) {
      this.form.controls.section_no.setErrors({ duplicate: true });
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.checkForErrors();
      return;
    }

    this.saving = true;
    this.saveError = '';

    this.projectsService.createSubmittalsRegistryItem({ ...values, projectId: this.data.projectId }).subscribe({
      next: (response: { data?: SubmittalsRegistryEntry }) => {
        this.saving = false;
        this.dialogRef.close(response?.data ?? null);
      },
      error: (err: { error?: { message?: string } }) => {
        this.saving = false;
        this.saveError = err?.error?.message || 'Could not add the registry entry. Please try again.';
      },
    });
  }

  private trimmedValues(): RegistryFormValues {
    const raw = this.form.getRawValue();
    const trim = (v: string | null) => v?.trim() || null;
    return {
      section_no: trim(raw.section_no),
      sub_section_no: trim(raw.sub_section_no),
      title: trim(raw.title),
      description: trim(raw.description),
    };
  }

  private isDuplicate(values: RegistryFormValues): boolean {
    if (!values.section_no) {
      return false;
    }
    const norm = (v: string | null) => (v ?? '').toString().trim().toLowerCase();
    return (this.data.existingRegistries || []).some(
      r => norm(r.section_no) === norm(values.section_no) && norm(r.sub_section_no) === norm(values.sub_section_no),
    );
  }
}
